// src/components/MarketingAttributionTracker.jsx
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../services/supabaseClient";

export const MARKETING_VISITOR_TOKEN_KEY =
  "uniair_marketing_visitor_token";
export const MARKETING_SESSION_TOKEN_KEY =
  "uniair_marketing_session_token";
export const MARKETING_VISIT_ID_KEY =
  "uniair_marketing_visit_id";
export const MARKETING_PENDING_KEY =
  "uniair_marketing_pending_attribution";

const STAFF_WORKSPACE_PREFIXES = [
  "/admin",
  "/marketing-admin",
  "/finance-admin",
  "/operations-admin",
  "/promoter",
];

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Attribution must never block the customer journey.
  }
};

const createUuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

const getOrCreateToken = (
  storage,
  key,
) => {
  const existing = safeGet(storage, key);
  if (existing) return existing;

  const token = createUuid();
  safeSet(storage, key, token);
  return token;
};

const getReferrerHostname = () => {
  try {
    return document.referrer
      ? new URL(document.referrer).hostname
      : "";
  } catch {
    return "";
  }
};

const getDeviceType = () => {
  const width = Number(window.innerWidth || 0);
  const userAgent = String(
    window.navigator?.userAgent || "",
  ).toLowerCase();

  if (/ipad|tablet/.test(userAgent)) {
    return "tablet";
  }

  if (
    /android|iphone|ipod|mobile/.test(userAgent)
    || width < 768
  ) {
    return "mobile";
  }

  return "desktop";
};

// Record each campaign touch for logged-in students, including returning riders.
// This supplements first-acquisition attribution without replacing it.
const claimCampaignTouchForUser = async (userId, visitId, visitorToken) => {
  if (!userId || !visitId || !visitorToken) return;
  const claimKey = `uniair_marketing_touch_${userId}_${visitId}`;
  if (safeGet(window.sessionStorage, claimKey) === "1") return;

  try {
    const { data, error } = await supabase.rpc(
      "marketing_claim_my_campaign_touch_v1",
      {
        p_visit_id: visitId,
        p_visitor_token: visitorToken,
      },
    );
    if (error) throw error;
    if (data?.claimed === true) {
      safeSet(window.sessionStorage, claimKey, "1");
    }
  } catch (error) {
    // A marketing issue must never prevent booking.
    console.warn("Marketing campaign touch claim failed:", error);
  }
};

export default function MarketingAttributionTracker() {
  const location = useLocation();
  const { user } = useAuth();

  const tracking = useMemo(() => {
    const params = new URLSearchParams(
      location.search || "",
    );

    return {
      linkCode: String(params.get("mc") || "")
        .trim()
        .toUpperCase(),
      utmSource: String(
        params.get("utm_source") || "",
      ).trim(),
      utmMedium: String(
        params.get("utm_medium") || "",
      ).trim(),
      utmCampaign: String(
        params.get("utm_campaign") || "",
      ).trim(),
      utmContent: String(
        params.get("utm_content") || "",
      ).trim(),
      utmTerm: String(
        params.get("utm_term") || "",
      ).trim(),
    };
  }, [location.search]);

  useEffect(() => {
    if (
      !tracking.linkCode
      && !tracking.utmCampaign
    ) {
      return;
    }

    let cancelled = false;

    const capture = async () => {
      const visitorToken = getOrCreateToken(
        window.localStorage,
        MARKETING_VISITOR_TOKEN_KEY,
      );
      const sessionToken = getOrCreateToken(
        window.sessionStorage,
        MARKETING_SESSION_TOKEN_KEY,
      );
      const fingerprint = [
        location.pathname,
        location.search,
        sessionToken,
      ].join("|");
      const previousFingerprint = safeGet(
        window.sessionStorage,
        "uniair_marketing_capture_fingerprint",
      );

      if (previousFingerprint === fingerprint) {
        return;
      }

      const { data, error } = await supabase.rpc(
        "marketing_capture_campaign_visit_v1",
        {
          p_payload: {
            visitor_token: visitorToken,
            session_token: sessionToken,
            link_code: tracking.linkCode || null,
            landing_path:
              location.pathname || "/",
            referrer_hostname:
              getReferrerHostname() || null,
            utm_source:
              tracking.utmSource || null,
            utm_medium:
              tracking.utmMedium || null,
            utm_campaign:
              tracking.utmCampaign || null,
            utm_content:
              tracking.utmContent || null,
            utm_term:
              tracking.utmTerm || null,
            device_type: getDeviceType(),
            language_code:
              window.navigator?.language || null,
          },
        },
      );

      if (cancelled) return;

      if (error) {
        console.warn(
          "Marketing campaign visit capture failed:",
          error,
        );
        return;
      }

      safeSet(
        window.sessionStorage,
        "uniair_marketing_capture_fingerprint",
        fingerprint,
      );

      if (data?.tracked === true && data?.visit_id) {
        safeSet(
          window.sessionStorage,
          MARKETING_VISIT_ID_KEY,
          data.visit_id,
        );
        safeSet(
          window.localStorage,
          MARKETING_VISIT_ID_KEY,
          data.visit_id,
        );
        safeSet(
          window.localStorage,
          MARKETING_PENDING_KEY,
          JSON.stringify({
            visit_id: data.visit_id,
            visitor_token: visitorToken,
            campaign_id:
              data.campaign_id || null,
            link_id: data.link_id || null,
            captured_at:
              new Date().toISOString(),
          }),
        );
        if (user?.id) {
          void claimCampaignTouchForUser(user.id, data.visit_id, visitorToken);
        }
      }
    };

    void capture();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    tracking.linkCode,
    tracking.utmCampaign,
    tracking.utmContent,
    tracking.utmMedium,
    tracking.utmSource,
    tracking.utmTerm,
    user?.id,
  ]);

  // Attribution is persisted across login/register even if the first
  // tracked visit happened before authentication.
  useEffect(() => {
    if (!user?.id) return;
    let pending = null;
    try {
      pending = JSON.parse(
        safeGet(window.localStorage, MARKETING_PENDING_KEY) || "null",
      );
    } catch {
      return;
    }
    const visitorToken = safeGet(
      window.localStorage,
      MARKETING_VISITOR_TOKEN_KEY,
    );
    if (pending?.visit_id && pending?.visitor_token === visitorToken) {
      void claimCampaignTouchForUser(user.id, pending.visit_id, visitorToken);
    }
  }, [user?.id, location.pathname]);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;

    const claim = async () => {
      const visitorToken = safeGet(
        window.localStorage,
        MARKETING_VISITOR_TOKEN_KEY,
      );
      const claimKey =
        `uniair_marketing_claimed_${user.id}`;
      const alreadyClaimed = safeGet(
        window.sessionStorage,
        claimKey,
      );

      if (alreadyClaimed === "1") return;

      const { data, error } = await supabase.rpc(
        "marketing_claim_my_acquisition_v1",
        {
          p_visitor_token: visitorToken || null,
        },
      );

      if (cancelled) return;

      if (error) {
        console.warn(
          "Marketing acquisition claim failed:",
          error,
        );
        return;
      }

      if (data?.claimed === true) {
        safeSet(
          window.sessionStorage,
          claimKey,
          "1",
        );
      }
    };

    // Acquisition claiming is attribution housekeeping, not a paint-critical
    // dependency. Defer it so it does not compete with dashboard/profile RPCs.
    const timer = window.setTimeout(() => {
      void claim();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user?.id, location.pathname]);

  useEffect(() => {
    if (!user?.id) return;

    const path = location.pathname || "/";
    const isStaffWorkspace = STAFF_WORKSPACE_PREFIXES.some(
      (prefix) =>
        path === prefix || path.startsWith(`${prefix}/`),
    );

    if (isStaffWorkspace) return;

    let cancelled = false;

    const recordAuthenticatedUsage = async () => {
      const activityDate = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Africa/Johannesburg",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
      const fingerprint = `uniair_authenticated_usage_${user.id}_${activityDate}_${path}`;

      if (
        safeGet(window.sessionStorage, fingerprint) === "1"
      ) {
        return;
      }

      const { error } = await supabase.rpc(
        "marketing_record_my_activity_v1",
        {
          p_path: path,
        },
      );

      if (cancelled) return;

      if (error) {
        // This metric is supplemental and must never interfere with the
        // customer or driver journey. It also keeps older deployments usable
        // while the accompanying SQL migration is being applied.
        console.warn(
          "Authenticated UniAir activity tracking failed:",
          error,
        );
        return;
      }

      safeSet(window.sessionStorage, fingerprint, "1");
    };

    const timer = window.setTimeout(() => {
      void recordAuthenticatedUsage();
    }, 1800);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user?.id, location.pathname]);

  return null;
}
